/**
 * useAutoProvisioning Hook
 * Main workflow orchestration hook
 */
import { useState, useCallback, useMemo, useRef } from "react";
import { useCspApi } from "@perfectserve/http-ps";
import { createProvisioningService } from "../services/provisioningService";
import { useXlsxParser } from "./useXlsxParser";
import { useValidation } from "./useValidation";
import { useJobMonitor } from "./useJobMonitor";
import { mergeWorkflowResults } from "../utils/resultsXlsxGenerator";
/**
 * Main hook for auto-provisioning workflow
 */
export function useAutoProvisioning() {
    const { get, post, put, getAuthToken } = useCspApi();
    // Workflow state
    const [currentStep, setCurrentStep] = useState("upload");
    const [jobId, setJobId] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isCopyingReferenceUser, setIsCopyingReferenceUser] = useState(false);
    const [copyReferenceUserStatus, setCopyReferenceUserStatus] = useState("");
    const [copyReferenceUserError, setCopyReferenceUserError] = useState(null);
    const [copyUserJobId, setCopyUserJobId] = useState(null);
    const [isValidationOnly, setIsValidationOnly] = useState(false);
    const [recordsWithResults, setRecordsWithResults] = useState([]);
    // Account mapping state
    const [isAccountMapping, setIsAccountMapping] = useState(false);
    const [accountMappingResult, setAccountMappingResult] = useState(null);
    const [accountMappingError, setAccountMappingError] = useState(null);
    // Sub-hooks
    const xlsxParser = useXlsxParser();
    const validation = useValidation();
    const jobMonitor = useJobMonitor();
    // Create provisioning service - uses env-ps for configuration
    const provisioningService = useMemo(() => createProvisioningService(getAuthToken), [getAuthToken]);
    // Store parsed records in a ref for immediate access
    const parsedRecordsRef = useRef([]);
    // Parse file and start validation immediately
    const parseFile = useCallback(async (file, validationOnly = false) => {
        setIsValidationOnly(validationOnly);
        const result = await xlsxParser.parseFile(file);
        if (!result) {
            // Parsing failed, error is set in xlsxParser
            return;
        }
        // Store records in ref for immediate access
        parsedRecordsRef.current = result.records;
        // Check for missing required fields
        if (result.missingRequiredFields.length > 0) {
            // Stay on upload step with error
            return;
        }
        // Check if we have any records
        if (result.records.length === 0) {
            // No data rows in file
            return;
        }
        // Start validation immediately with the returned records
        setCurrentStep("validation");
        const errors = await validation.validateRecords(result.records);
        if (errors.length === 0) {
            // If validation only mode, stop here and wait for user to continue
            if (validationOnly) {
                // Stay on validation step - user will click Continue
                return;
            }
            // Start account mapping step
            await performAccountMappingAndUpload(result.records);
        }
    }, [xlsxParser, validation]);
    // Perform account mapping and then upload
    const performAccountMappingAndUpload = useCallback(async (records) => {
        // Account mapping step
        setCurrentStep("account-mapping");
        setIsAccountMapping(true);
        setAccountMappingError(null);
        setAccountMappingResult(null);
        try {
            // Get account info from first record
            const firstRecord = records[0];
            const accountId = firstRecord?.referenceUserAccountId;
            const accountName = firstRecord?.referenceUserAccountName;
            if (accountId && accountName) {
                const mappingResult = await provisioningService.ensureAccountMapping(accountId, accountName);
                setAccountMappingResult(mappingResult);
                if (!mappingResult.success) {
                    setAccountMappingError(mappingResult.message);
                    setCurrentStep("error");
                    return;
                }
            }
            else {
                // No account mapping needed (no reference user specified)
                setAccountMappingResult({
                    success: true,
                    wasAdded: false,
                    message: "No account mapping required",
                });
            }
        }
        catch (err) {
            setAccountMappingError(err instanceof Error ? err.message : "Account mapping failed");
            setCurrentStep("error");
            return;
        }
        finally {
            setIsAccountMapping(false);
        }
        // Proceed to upload
        setCurrentStep("uploading");
        setIsUploading(true);
        setUploadError(null);
        try {
            const uploadResult = await provisioningService.uploadCSV(records);
            if (uploadResult.success && uploadResult.jobId) {
                setJobId(uploadResult.jobId);
                setCurrentStep("job-monitoring");
            }
            else {
                setUploadError(uploadResult.message || "Upload failed");
                setCurrentStep("error");
            }
        }
        catch (err) {
            setUploadError(err instanceof Error ? err.message : "Upload failed");
            setCurrentStep("error");
        }
        finally {
            setIsUploading(false);
        }
    }, [provisioningService]);
    // Continue after validation (for validation only mode)
    const continueAfterValidation = useCallback(async () => {
        const records = parsedRecordsRef.current.length > 0 ? parsedRecordsRef.current : xlsxParser.records;
        if (records.length === 0) {
            return;
        }
        setIsValidationOnly(false);
        // Use the account mapping flow
        await performAccountMappingAndUpload(records);
    }, [xlsxParser.records, performAccountMappingAndUpload]);
    // Internal upload function - uses ref for records (now goes through account mapping)
    const startUploadInternal = useCallback(async () => {
        const records = parsedRecordsRef.current.length > 0 ? parsedRecordsRef.current : xlsxParser.records;
        await performAccountMappingAndUpload(records);
    }, [performAccountMappingAndUpload, xlsxParser.records]);
    // Start validation (for manual retry)
    const startValidation = useCallback(async () => {
        const records = parsedRecordsRef.current.length > 0 ? parsedRecordsRef.current : xlsxParser.records;
        setCurrentStep("validation");
        const errors = await validation.validateRecords(records);
        if (errors.length === 0) {
            await performAccountMappingAndUpload(records);
        }
    }, [validation, xlsxParser.records, startUploadInternal]);
    // Start upload (public API)
    const startUpload = startUploadInternal;
    // Start job monitoring
    const startMonitoring = useCallback(async () => {
        if (!jobId)
            return;
        const result = await jobMonitor.startMonitoring(jobId);
        // Merge workflow results into records
        const records = parsedRecordsRef.current.length > 0 ? parsedRecordsRef.current : xlsxParser.records;
        const mergedRecords = mergeWorkflowResults(records, result.createdUsers);
        setRecordsWithResults(mergedRecords);
        // Only continue to copy reference user if we actually created users
        if (result.successfullyCreatedCount > 0) {
            setCurrentStep("copy-reference-user");
        }
        else if (result.createdUsers.length > 0) {
            // We have practitioners but none were created (all warnings/errors)
            // Skip copy reference user and go to complete with warning state
            setCurrentStep("complete");
        }
        else {
            setCurrentStep("error");
        }
    }, [jobId, jobMonitor, xlsxParser.records]);
    // Start copy reference user
    const startCopyReferenceUser = useCallback(async () => {
        setIsCopyingReferenceUser(true);
        setCopyReferenceUserStatus("Starting...");
        setCopyReferenceUserError(null);
        setCopyUserJobId(null);
        try {
            // Get reference user V5 ID from first record
            const referenceV5Id = xlsxParser.records[0]?.referenceUserV5Id;
            if (!referenceV5Id) {
                setCopyReferenceUserStatus("Skipped - no reference user V5 ID");
                setCurrentStep("complete");
                return;
            }
            // Get target user IDs
            const targetUserIds = jobMonitor.createdUsers.map((u) => u.userId).filter((id) => id);
            if (targetUserIds.length === 0) {
                setCopyReferenceUserStatus("Skipped - no target users");
                setCurrentStep("complete");
                return;
            }
            const result = await provisioningService.executeCopyReferenceUser(referenceV5Id, targetUserIds, (status) => setCopyReferenceUserStatus(status));
            // Store the job ID for display
            if (result.jobId) {
                setCopyUserJobId(result.jobId);
            }
            if (result.success) {
                setCopyReferenceUserStatus(`Complete - Job ID: ${result.jobId || "N/A"}`);
                setCurrentStep("complete");
            }
            else {
                setCopyReferenceUserError(`Copy reference user failed${result.jobId ? ` (Job ID: ${result.jobId})` : ""}`);
                setCurrentStep("complete"); // Still complete, but with warning
            }
        }
        catch (err) {
            setCopyReferenceUserError(err instanceof Error ? err.message : "Copy reference user failed");
            setCurrentStep("complete");
        }
        finally {
            setIsCopyingReferenceUser(false);
        }
    }, [xlsxParser.records, jobMonitor.createdUsers, provisioningService]);
    // Reset workflow
    const reset = useCallback(() => {
        setCurrentStep("upload");
        setJobId(null);
        setIsUploading(false);
        setUploadError(null);
        setIsCopyingReferenceUser(false);
        setCopyReferenceUserStatus("");
        setCopyReferenceUserError(null);
        setCopyUserJobId(null);
        setIsValidationOnly(false);
        setIsAccountMapping(false);
        setAccountMappingResult(null);
        setAccountMappingError(null);
        setRecordsWithResults([]);
        xlsxParser.clear();
        validation.clear();
        jobMonitor.clear();
    }, [xlsxParser, validation, jobMonitor]);
    return {
        currentStep,
        setCurrentStep,
        // File parsing
        records: xlsxParser.records,
        recordsWithResults,
        columnMapping: xlsxParser.columnMapping,
        headers: xlsxParser.headers,
        isParsing: xlsxParser.isParsing,
        parseError: xlsxParser.error,
        missingRequiredFields: xlsxParser.missingRequiredFields,
        parseFile,
        isValidationOnly,
        continueAfterValidation,
        // Validation
        isValidating: validation.isValidating,
        validationCurrentRow: validation.currentRow,
        validationTotalRows: validation.totalRows,
        validationErrors: validation.errors,
        isValid: validation.isValid,
        startValidation,
        // Upload
        isUploading,
        uploadError,
        jobId,
        startUpload,
        // Job monitoring
        jobStatus: jobMonitor.status,
        isMonitoring: jobMonitor.isMonitoring,
        createdUsers: jobMonitor.createdUsers,
        successfullyCreatedCount: jobMonitor.successfullyCreatedCount,
        hasWarnings: jobMonitor.hasWarnings,
        monitoringError: jobMonitor.error,
        startMonitoring,
        // Account Mapping
        isAccountMapping,
        accountMappingResult,
        accountMappingError,
        // Copy Reference User
        isCopyingReferenceUser,
        copyReferenceUserStatus,
        copyReferenceUserError,
        copyUserJobId,
        startCopyReferenceUser,
        // General
        environment: provisioningService.environment,
        reset,
    };
}
